
import React, { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { useClientPaymentHistory, ClientPayment } from '@/hooks/clients/useClientPaymentHistory';
import { useUpdateClientPayment } from '@/hooks/clients/useUpdateClientPayment';
import { useDeleteClientPayment } from '@/hooks/clients/useDeleteClientPayment';
import { Edit, Trash2, Euro, Calendar, Tag } from 'lucide-react';
import { formatPaymentDate, formatLocalizedDate } from '@/utils/dateHelpers';

interface PaymentHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
}

const PaymentHistoryDialog: React.FC<PaymentHistoryDialogProps> = ({
  open,
  onOpenChange,
  clientId,
  clientName,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const [editingPayment, setEditingPayment] = useState<ClientPayment | null>(null);
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  
  const { data: payments = [], isLoading, error } = useClientPaymentHistory(clientId);
  const updatePaymentMutation = useUpdateClientPayment();
  const deletePaymentMutation = useDeleteClientPayment();

  const handleEditPayment = (payment: ClientPayment) => {
    setEditingPayment(payment);
    setEditAmount(payment.amount.toString());
    setEditDate(payment.paymentDate);
    setEditNotes(payment.notes || '');
  };

  const handleSaveEdit = () => {
    if (!editingPayment) return;

    updatePaymentMutation.mutate({
      paymentId: editingPayment.id,
      paymentData: {
        amount: parseFloat(editAmount) || 0,
        paymentDate: editDate,
        notes: editNotes,
      }
    }, {
      onSuccess: () => {
        setEditingPayment(null);
        setEditAmount('');
        setEditDate('');
        setEditNotes('');
      }
    });
  };

  const handleCancelEdit = () => {
    setEditingPayment(null);
    setEditAmount('');
    setEditDate('');
    setEditNotes('');
  };

  const handleDeleteConfirm = () => {
    if (!deletePaymentId) return;
    
    deletePaymentMutation.mutate(deletePaymentId, {
      onSuccess: () => {
        setDeletePaymentId(null);
      }
    });
  };

  const content = (
    <div className="space-y-4">
      {isLoading && (
        <div className="text-center text-muted-foreground py-8">
          {t('common.loading')}
        </div>
      )}

      {error && (
        <div className="text-center text-destructive py-8">
          {t('clients.loadError')}
        </div>
      )}

      {!isLoading && !error && payments.length === 0 && (
        <div className="text-center text-muted-foreground py-8">
          {t('clients.noPaymentsFound')}
        </div>
      )}

      {!isLoading && !error && payments.length > 0 && (
        <div className="space-y-3">
          {payments.map((payment) => (
            <Card key={payment.id}>
              <CardContent className="p-4">
                {editingPayment?.id === payment.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="edit-amount">{t('clients.paymentAmount')}</Label>
                        <Input
                          id="edit-amount"
                          type="number"
                          step="0.01"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-date">{t('common.date')}</Label>
                        <Input
                          id="edit-date"
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-notes">{t('clients.paymentNotes')}</Label>
                      <Textarea
                        id="edit-notes"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder={t('clients.paymentNotesPlaceholder')}
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={updatePaymentMutation.isPending}
                      >
                        {updatePaymentMutation.isPending ? t('common.saving') : t('common.save')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Euro className="h-4 w-4 text-green-600" />
                        <div className="font-medium">
                          €{payment.amount.toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <strong>{t('common.date')}:</strong>
                          {formatPaymentDate(payment.paymentDate, i18n.language)}
                        </div>
                        
                        {/* Show subscription info */}
                        {payment.subscription_workout_type_name && (
                          <div className="text-xs text-purple-600 bg-purple-100 dark:bg-purple-900/20 p-1.5 rounded-md mt-1">
                            <div className="flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              <strong>{t('clients.subscription')}:</strong> {payment.subscription_workout_type_name}
                            </div>
                          </div>
                        )}
                        
                        {/* Show session info if it's a session payment */}
                        {payment.session_workout_name && payment.session_booking_date && (
                           <div className="text-xs text-blue-600 bg-blue-100 dark:bg-blue-900/20 p-1.5 rounded-md mt-1">
                             <strong>{t('clients.paidForSession')}:</strong> {payment.session_workout_name} ({formatLocalizedDate(payment.session_booking_date, i18n.language)})
                           </div>
                        )}
                        
                        {payment.notes && (
                          <div>
                            <strong>{t('clients.paymentNotes')}:</strong> {payment.notes}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditPayment(payment)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeletePaymentId(payment.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isFullScreen ? (
        <Sheet open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
          <SheetContent 
            side="bottom" 
            className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-[300px] rounded-t-lg'}`}
          >
            <SheetHeader className={`text-left ${isTablet ? 'p-6 border-b flex-shrink-0' : ''}`}>
              <SheetTitle>{t('common.confirmDelete')}</SheetTitle>
              <SheetDescription>
                {t('clients.deletePaymentConfirmation')}
              </SheetDescription>
            </SheetHeader>
            <div className={`flex flex-col gap-3 ${isTablet ? 'flex-1 p-6 pb-safe-bottom justify-center' : 'mt-6'}`}>
              <Button 
                onClick={handleDeleteConfirm}
                variant="destructive" 
                className="w-full"
                disabled={deletePaymentMutation.isPending}
              >
                {deletePaymentMutation.isPending ? t('common.deleting') : t('common.delete')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setDeletePaymentId(null)}
                className="w-full"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <AlertDialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
          <AlertDialogContent className="w-full max-w-md mx-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('clients.deletePaymentConfirmation')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
              <AlertDialogCancel className="w-full sm:w-auto">
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteConfirm}
                className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deletePaymentMutation.isPending}
              >
                {deletePaymentMutation.isPending ? t('common.deleting') : t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
      >
        <SheetHeader className="p-6 border-b flex-shrink-0 pt-[calc(1.5rem+env(safe-area-inset-top))] pwa-safe-header">
          <SheetTitle className="greek-text">
            {t('clients.paymentHistory')} - {clientName}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 p-6 overflow-y-auto pb-safe-bottom">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PaymentHistoryDialog;
